import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearch } from 'src/Utils/search/search.hooks'
import { useGetURLParams } from 'src/Utils/dashboards/dashboards.hooks'
import { useSearchContext } from 'src/Utils/search/search.context'
import { AdvancedSearchOptions } from 'src/Utils/search/search.types'
import {
  CloseIcon,
  SearchBar,
  SearchContainer,
  SearchIcon,
  SearchIconButton,
  SearchIconText,
  SearchInput,
  SearchInputContainer,
  SearchOption,
  SearchOptionsContainer,
} from './Search.styles'
import { AdvancedSearch } from './AdvancedSearch/AdvancedSearch'
import { ContentSearch } from './ContentSearch/ContentSearch'

export const Search = ({
  isExpanded,
  setIsExpanded,
  withContentSearch,
  withAdvancedSearch,
  paramPrefix,
  onFocus,
  onBlur,
  onSearch,
  onChange,
  disableEnter,
}: {
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
  withContentSearch?: boolean
  withAdvancedSearch?: boolean
  paramPrefix?: string
  onFocus?: () => void
  onBlur?: () => void
  onSearch?: (
    query?: string,
    advancedSearch?: AdvancedSearchOptions,
    isContentSearchActive?: boolean,
  ) => void
  onChange?: (query: string) => void
  disableEnter?: boolean
}): JSX.Element => {
  const searchBarRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false)
  const search = useSearch(paramPrefix)
  const { setContentMatchIds } = useSearchContext()
  const searchParams = useGetURLParams()
  const searchParamValue = useMemo(
    () =>
      searchParams[paramPrefix ? `${paramPrefix}Search` : 'search'] as string,
    [searchParams, paramPrefix],
  )
  const isContentSearchActive = useMemo(() => {
    return (
      searchParams[
        paramPrefix ? `${paramPrefix}ContentSearch` : 'contentSearch'
      ] === 'true'
    )
  }, [searchParams, paramPrefix])

  useEffect(() => {
    if (searchParamValue) {
      setQuery(decodeURIComponent(searchParamValue))
      setIsExpanded(true)
    } else if (
      !searchInputRef.current?.contains(document.activeElement) &&
      !isAdvancedSearchOpen
    ) {
      setQuery('')
      setIsExpanded(false)
      setContentMatchIds(new Set())
    }
  }, [
    searchParamValue,
    setIsExpanded,
    setContentMatchIds,
    isAdvancedSearchOpen,
  ])

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !disableEnter) {
      search(query, undefined, isContentSearchActive)
      onSearch?.(query, undefined, isContentSearchActive)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    onChange?.(e.target.value)
  }

  const handleClear = useCallback(() => {
    setQuery('')
    searchParamValue && search('', undefined, isContentSearchActive)
    setContentMatchIds(new Set())
    onChange?.('')
  }, [
    search,
    isContentSearchActive,
    setContentMatchIds,
    onChange,
    searchParamValue,
  ])

  const onClear = useCallback(() => {
    handleClear()
    setIsExpanded(true)
    searchInputRef.current?.focus()
  }, [handleClear, setIsExpanded])

  return (
    <SearchContainer $isExpanded={isExpanded}>
      <SearchBar ref={searchBarRef}>
        <SearchInputContainer>
          <SearchIconButton
            onClick={() => {
              if (isExpanded) {
                // Blur the input first to ensure clean state
                searchInputRef.current?.blur()
                handleClear()
                onBlur?.()
                setIsExpanded(false)
              } else {
                setIsExpanded(true)
                searchInputRef.current?.focus()
              }
            }}
            onMouseDown={(e) => {
              // Prevent default to avoid focus issues
              if (isExpanded) {
                e.preventDefault()
              }
            }}
          >
            <SearchIcon />
            <SearchIconText>Search</SearchIconText>
          </SearchIconButton>
          <SearchInput
            value={query}
            onChange={handleChange}
            onKeyDown={handleEnter}
            ref={searchInputRef}
            onFocus={onFocus}
            onBlur={() => {
              if (!query && !isAdvancedSearchOpen) {
                setIsExpanded(false)
              }
              onBlur?.()
            }}
          />
          {query.length > 0 && (
            <CloseIcon
              onClick={onClear}
              onMouseDown={(e) => e.preventDefault()}
            />
          )}
        </SearchInputContainer>
        <SearchOptionsContainer>
          {withContentSearch && (
            <SearchOption>
              <ContentSearch query={query} />
            </SearchOption>
          )}
          {withAdvancedSearch && (
            <SearchOption>
              <AdvancedSearch
                anchorRef={searchBarRef}
                query={query}
                onSearch={(query, advancedSearch, isContentSearchActive) => {
                  search(query, advancedSearch, isContentSearchActive)
                  onSearch?.(query, advancedSearch, isContentSearchActive)
                }}
                onMouseDown={(e) => e.preventDefault()}
                onOpenChange={setIsAdvancedSearchOpen}
              />
            </SearchOption>
          )}
        </SearchOptionsContainer>
      </SearchBar>
    </SearchContainer>
  )
}
